import { ROUTES } from '#/config/routes';
import {
  searchProcessingFactoryFilterOptions,
} from '#/api/momMgmt/settings/processingFactory/fetchSelectOptions';
import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import { searchWarehouseFilterOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import WorkOrderStatusBadge from '#/modules/momMgmt/workOrder/components/WorkOrderStatusBadge';
import {
  readScheduledStartDateRangeFilter,
  resolveScheduledStartDateFilterChipDisplay,
} from '#/modules/momMgmt/workOrder/helper/scheduledStartDateListFilter';
import { WORK_ORDER_STATUS_FILTER_OPTIONS } from '#/modules/momMgmt/workOrder/helper/workOrderStatus';
import { formatDateOrDash } from '#/utils/date';
import { formatDisplayQty } from '#/utils/formatNumber';

const DATE_FILTER_PLACEHOLDER = '西元年/月/日';

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return value;
}

function formatMtrLabel(mtr) {
  if (!mtr) {
    return '-';
  }
  return mtr.mtrNo ? `${mtr.mtrNo} ${mtr.name ?? ''}`.trim() : displayDash(mtr.name);
}

function formatBomLabel(bom) {
  if (!bom) {
    return '-';
  }
  const code = bom.code ?? '';
  const version = bom.version ? ` ${bom.version}` : '';
  return displayDash(`${code}${version}`.trim() || bom.name);
}

function formatEntityLabel(entity) {
  if (!entity) {
    return '-';
  }
  const label = [entity.code, entity.name].filter(Boolean).join(' ');
  return label || '-';
}

function formatQtySummary(quantity) {
  if (!quantity) {
    return '-';
  }
  const manufacturing = quantity.manufacturingQty;
  const effective = quantity.effectiveManufacturingQty;
  if (manufacturing == null && effective == null) {
    return '-';
  }
  if (effective != null && manufacturing != null && Number(effective) !== Number(manufacturing)) {
    return `${formatDisplayQty(manufacturing)}（有效 ${formatDisplayQty(effective)}）`;
  }
  return formatDisplayQty(manufacturing ?? effective);
}

function formatCounts(row) {
  const amendment = row.amendmentCount ?? 0;
  const issue = row.stockIssueCount ?? 0;
  const receipt = row.stockReceiptCount ?? 0;
  return `異動 ${amendment}／出庫 ${issue}／入庫 ${receipt}`;
}

export function buildWorkOrderColumns({
  filters = {},
  onFilterChange,
  onScheduledStartDateRangeChange,
  processingFactoryLabelCache,
  workWarehouseLabelCache,
  targetWarehouseLabelCache,
  canShowEdit,
} = {}) {
  return [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.workOrderDetail(row.id),
      getEditPath: (row) => ROUTES.workOrderEdit(row.id),
      canShowEdit,
    }),
    {
      id: 'workOrderNo',
      header: '工單號',
      accessorKey: 'workOrderNo',
      accessorFn: (row) => displayDash(row.workOrderNo),
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'workOrderNo',
        label: '工單號',
        filterValue: filters.workOrderNo,
        onChange: (value) => onFilterChange('workOrderNo', value),
        placeholder: '搜尋工單號',
      }),
    },
    {
      id: 'scheduledStartDate',
      header: '排程日',
      accessorKey: 'scheduledStartDate',
      accessorFn: (row) => formatDateOrDash(row.scheduledStartDate),
      size: 120,
      minSize: 120,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'scheduledStartDateFrom',
        label: '排程日',
        mode: 'range',
        filterValue: readScheduledStartDateRangeFilter(filters),
        onChange: onScheduledStartDateRangeChange,
        placeholder: DATE_FILTER_PLACEHOLDER,
        resolveFilterDisplay: resolveScheduledStartDateFilterChipDisplay,
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 100,
      cell: ({ row }) => (
        row.original.status
          ? <WorkOrderStatusBadge status={row.original.status} size="sm" />
          : '-'
      ),
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: WORK_ORDER_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
    },
    {
      id: 'mtr',
      header: '成品',
      accessorFn: (row) => formatMtrLabel(row.mtr),
      size: 180,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'mtrNo',
          label: '成品',
          filterValue: filters.mtrNo,
          onChange: (value) => onFilterChange('mtrNo', value),
          placeholder: '搜尋料號或名稱',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={formatMtrLabel(row.original.mtr)} />
      ),
    },
    {
      id: 'bom',
      header: 'BOM',
      accessorFn: (row) => formatBomLabel(row.bom),
      size: 140,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'bomCode',
          label: 'BOM',
          filterValue: filters.bomCode,
          onChange: (value) => onFilterChange('bomCode', value),
          placeholder: '搜尋 BOM 代號',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={formatBomLabel(row.original.bom)} />
      ),
    },
    {
      id: 'processingFactory',
      header: '加工廠',
      accessorFn: (row) => formatEntityLabel(row.processingFactory),
      size: 140,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'processingFactoryId',
          label: '加工廠',
          searchOptions: searchProcessingFactoryFilterOptions,
          filterValue: filters.processingFactoryId,
          onChange: (value) => onFilterChange('processingFactoryId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(processingFactoryLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(processingFactoryLabelCache, options, values);
          },
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip
          text={formatEntityLabel(row.original.processingFactory)}
        />
      ),
    },
    {
      id: 'workWarehouse',
      header: '工作倉',
      accessorFn: (row) => formatEntityLabel(row.workWarehouse),
      size: 120,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'workWarehouseId',
          label: '工作倉',
          searchOptions: searchWarehouseFilterOptions,
          filterValue: filters.workWarehouseId,
          onChange: (value) => onFilterChange('workWarehouseId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(workWarehouseLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(workWarehouseLabelCache, options, values);
          },
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip
          text={formatEntityLabel(row.original.workWarehouse)}
        />
      ),
    },
    {
      id: 'targetWarehouse',
      header: '目標倉',
      accessorFn: (row) => formatEntityLabel(row.targetWarehouse),
      size: 120,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'targetWarehouseId',
          label: '目標倉',
          searchOptions: searchWarehouseFilterOptions,
          filterValue: filters.targetWarehouseId,
          onChange: (value) => onFilterChange('targetWarehouseId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(targetWarehouseLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(targetWarehouseLabelCache, options, values);
          },
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip
          text={formatEntityLabel(row.original.targetWarehouse)}
        />
      ),
    },
    {
      id: 'qtySummary',
      header: '製造數量',
      accessorFn: (row) => formatQtySummary(row.quantity),
      size: 140,
    },
    {
      id: 'docCounts',
      header: '單據筆數',
      accessorFn: (row) => formatCounts(row),
      size: 160,
      enableSorting: false,
    },
    {
      id: 'issueReason',
      header: '事由',
      accessorKey: 'issueReason',
      accessorFn: (row) => displayDash(row.issueReason),
      size: 160,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original.issueReason)} />
      ),
    },
    {
      id: 'remark',
      header: '備註',
      accessorKey: 'remark',
      accessorFn: (row) => displayDash(row.remark),
      size: 160,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original.remark)} />
      ),
    },
  ];
}
